import React from 'react';
import { useI18n } from '../i18n';
import { CheckCircle2 } from 'lucide-react';

export const ArchitectureView: React.FC = () => {
  const { t } = useI18n();

  return (
    <section id="architecture" className="py-20 md:py-28 relative bg-slate-100/70 dark:bg-navy-950/40 border-y border-slate-200 dark:border-navy-800/80 transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-500/10 border border-gold-500/30 text-xs font-semibold text-gold-600 dark:text-gold-400 mb-4 uppercase tracking-wider">
            <span>{t.architecture.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-4">
            {t.architecture.title}
          </h2>
          <p className="text-base text-slate-600 dark:text-slate-300">
            {t.architecture.subtitle}
          </p>
        </div>

        {/* 4-Step Interactive Pipeline Diagram */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Step 1 */}
          <div className="glow-card rounded-2xl p-6 relative border-slate-200 dark:border-navy-700/80">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-navy-800 border border-slate-300 dark:border-navy-700 flex items-center justify-center text-slate-800 dark:text-gold-400 font-bold mb-4 font-mono">
              01
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">{t.architecture.step1Title}</h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-4">{t.architecture.step1Desc}</p>
            <span className="text-[10px] font-mono text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-navy-900 px-2.5 py-1 rounded border border-slate-200 dark:border-navy-800 font-medium">
              SAML / LDAP Auth
            </span>
          </div>

          {/* Step 2 */}
          <div className="glow-card rounded-2xl p-6 relative border-gold-500/40 shadow-gold-glow">
            <div className="w-10 h-10 rounded-xl bg-gold-500/20 border border-gold-500/60 flex items-center justify-center text-gold-600 dark:text-gold-400 font-bold mb-4 font-mono">
              02
            </div>
            <h3 className="text-base font-bold text-gold-600 dark:text-gold-400 mb-2">{t.architecture.step2Title}</h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-4">{t.architecture.step2Desc}</p>
            <span className="text-[10px] font-mono text-gold-600 dark:text-gold-400 bg-gold-500/10 px-2.5 py-1 rounded border border-gold-500/30 font-medium">
              RAM Token Vault
            </span>
          </div>

          {/* Step 3 */}
          <div className="glow-card rounded-2xl p-6 relative border-slate-200 dark:border-navy-700/80">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-navy-800 border border-slate-300 dark:border-navy-700 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold mb-4 font-mono">
              03
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">{t.architecture.step3Title}</h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-4">{t.architecture.step3Desc}</p>
            <span className="text-[10px] font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2.5 py-1 rounded border border-blue-200 dark:border-blue-800/60 font-medium">
              AES-256 Egress
            </span>
          </div>

          {/* Step 4 */}
          <div className="glow-card rounded-2xl p-6 relative border-slate-200 dark:border-navy-700/80">
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-navy-800 border border-slate-300 dark:border-navy-700 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold mb-4 font-mono">
              04
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">{t.architecture.step4Title}</h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-4">{t.architecture.step4Desc}</p>
            <span className="text-[10px] font-mono text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/60 px-2.5 py-1 rounded border border-emerald-200 dark:border-emerald-800/60 font-medium">
              Dynamic Stream
            </span>
          </div>
        </div>

        {/* Security Assurance Banner */}
        <div className="max-w-3xl mx-auto bg-white dark:bg-navy-900/90 border border-slate-200 dark:border-navy-700/80 rounded-xl p-4 sm:p-5 flex items-center gap-3.5 text-xs sm:text-sm text-slate-700 dark:text-slate-300 shadow-sm">
          <CheckCircle2 className="w-5 h-5 text-gold-500 shrink-0" />
          <p className="leading-normal font-medium">{t.architecture.securityNote}</p>
        </div>
      </div>
    </section>
  );
};
